import { Fragment, useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import TablePagination from "../components/TablePagination";
import OuterBodyModal from "../components/modals/OuterBodyModal";
import { Button } from "react-bootstrap";
import AddEditProductDetails from "../components/modals/AddEditProductDetails";
import DeleteConfirmationModal from "../components/modals/DeleteConfirmationModal";
import { API_BASE_URL } from "../config";
import { read, utils } from "xlsx";
import ExportButton from "../components/ExportButton";
import DownloadTemplate from "../components/DownloadTemplate";
import AlertModal from "../components/modals/AlertModal";

const ProductDetails = () => {
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("Add Product Category");
  const [showDelModal, setShowDelModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const initialFormData = {
    id: "",
    productCategoryId: null,
    productName: "",
    productCode: "",
    hsnCode: "",
    rol: "",
    igst: "",
    sgst: "",
    cgst: "",
    purchasePrice: "",
    salesPrice: "",
    mrp: "",
    purchaseInclusive: 0,
    salesInclusive: 0,
    expiryDate: "",
    validationError: "",
  };
  const [formData, setFormData] = useState(initialFormData);

  const getProductDetailsData = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/getProductDetails`);
      setProducts(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getProductDetailsData();
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setShowDelModal(false);
    setShowAlertModal(false);
  }, []);

  const onClickAdd = useCallback(() => {
    setModalTitle("Add Product Details");
    setFormData(initialFormData);
    setShowModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickEdit = useCallback(
    (id) => {
      const getProductById = async (id) => {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/getProductDetailById?id=${id}`
          );
          // const { name } = response.data;
          setFormData({
            ...formData,
            id: response.data.id,
            productCategoryId: response.data.product_category_id,
            productName: response.data.product_name,
            productCode: response.data.product_code,
            hsnCode: response.data.hsn_code,
            rol: response.data.rol,
            igst: response.data.igst,
            sgst: response.data.sgst,
            cgst: response.data.cgst,
            purchasePrice: response.data.purchase_price,
            salesPrice: response.data.sales_price,
            mrp: response.data.mrp,
            purchaseInclusive: response.data.purchase_inclusive,
            salesInclusive: response.data.sales_inclusive,
            expiryDate: response.data.expiry_date,
          });
        } catch (error) {
          console.log(error);
        }
      };

      console.log("Edit ID: " + id);
      getProductById(id);
      setModalTitle("Edit Product Details");
      setShowModal(true);
    },
    [formData]
  );

  const onClickDel = useCallback(
    (id) => {
      setFormData({
        ...formData,
        id: id,
      });
      setShowDelModal(true);
    },
    [formData]
  );

  const onSubmitProducts = useCallback(() => {
    setShowModal(false);
    getProductDetailsData();
  }, []);

  const handleSubmitAlert = useCallback(
    async (event) => {
      event.preventDefault();
      const clickedButton = event.nativeEvent.submitter;
      const buttonValue = clickedButton.getAttribute("name");

      if (buttonValue === "Yes") {
        console.log("Yes button clicked and id is: " + formData.id);
        try {
          const res = await axios.post(
            `${API_BASE_URL}/api/delProductDetails`,
            {
              id: formData.id,
            }
          );
          if (res.data.message) {
            console.log(res.data.message);
            setAlertMessage("Product Details deleted successfully");
          } else if (res.data.error) {
            console.log(res.data.error);
            setAlertMessage("Error: " + res.data.error);
          }
        } catch (err) {
          console.log(err);
        }
        setShowDelModal(false);
        getProductDetailsData();
        setShowAlertModal(true);
      } else if (buttonValue === "No") {
        handleCloseModal();
      }
    },
    [formData, handleCloseModal]
  );

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  const handleFileUpload = async () => {
    try {
      if (!selectedFile) {
        alert("Please select a file to upload.");
        return;
      }

      const fileReader = new FileReader();
      fileReader.onload = async (event) => {
        const data = event.target.result;
        const workbook = read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const productDetailsData = utils.sheet_to_json(worksheet);

        // Call the backend API to upload the products data
        try {
          const response = await axios.post(
            `${API_BASE_URL}/api/uploadProductDetails`,
            {
              productDetailsData,
            }
          );
          console.log(response.data);
          alert(response.data.message);
          getProductDetailsData();
          // Optionally, you can display a success message or refresh the customer data after upload.
        } catch (error) {
          console.error("Error while uploading productCategories:", error);
          // Display an error message to the user
        }
      };

      fileReader.readAsBinaryString(selectedFile);
    } catch (error) {
      console.error("Error during file upload:", error);
      // Display an error message to the user
    }
  };

  const memoizedProducts = useMemo(() => products, [products]);
  return (
    <>
      {products ? (
        <Fragment>
          <Button className="m-3" onClick={onClickAdd}>
            + Add
          </Button>
          <br />
          <ExportButton data={memoizedProducts} name="ProductDetails" />
          <br />
          <input type="file" onChange={handleFileChange} className="m-3" />
          <Button onClick={handleFileUpload}>Upload</Button>
          <DownloadTemplate template_type="product_details"></DownloadTemplate>
          <TablePagination
            key={JSON.stringify(memoizedProducts)}
            tabledata={memoizedProducts}
            initialRowsPerPage="5"
            showEditOption={true}
            showDeleteOption={true}
            onClickEdit={onClickEdit}
            onClickDel={onClickDel}
          ></TablePagination>
          <OuterBodyModal
            modalTitle={modalTitle}
            showModal={showModal}
            handleClose={handleCloseModal}
          >
            <AddEditProductDetails
              initialFormData={formData}
              type={modalTitle}
              onHandleSubmit={onSubmitProducts}
            ></AddEditProductDetails>
          </OuterBodyModal>
          <DeleteConfirmationModal
            showModal={showDelModal}
            handleClose={handleCloseModal}
            handleSubmitAlert={handleSubmitAlert}
          />
          <AlertModal
            showModal={showAlertModal}
            handleClose={handleCloseModal}
            modalTitle="Alert"
            message={alertMessage}
          />
        </Fragment>
      ) : (
        <p>Loading...</p>
      )}
    </>
  );
};

export default ProductDetails;
